import { AutocompleteAddress } from '@popsure/dirty-swan';
import { Address, isPartialAddressValid } from '@getpopsure/public-models';
import {
  QuestionnaireFormProps,
  QuestionnaireQuestions,
} from '../../../models';
import { useEffect, useState } from 'react';
import { Header } from '../../Screen';

export type AddressFormProps<Questionnaire extends QuestionnaireQuestions> = {
  placeholders?: {
    manualAddressEntry?: string;
    street?: string;
    houseNumber?: string;
    additionalInformation?: string;
    postcode?: string;
    city?: string;
  };
  manualAddressEntryTexts?: { preText?: string; cta?: string };
  countryCode?: string;
} & QuestionnaireFormProps<Questionnaire, Partial<Address>>;

export const AddressForm = <Questionnaire extends QuestionnaireQuestions>({
  value,
  onSubmitValue,
  question,
  featureName,
  placeholders,
  manualAddressEntryTexts,
  setValidForSubmission,
  configuration,
  countryCode = 'DE',
}: AddressFormProps<Questionnaire>) => {
  const [address, setAddress] = useState<Partial<Address> | undefined>(value);

  const handleSubmit = () => {
    if (!address) {
      throw new Error(
        `[${featureName}] Address is missing for submission for question: ${question}`
      );
    }
    if (isPartialAddressValid(address)) {
      onSubmitValue(address);
    }
  };

  useEffect(() => {
    const isValid = address && isPartialAddressValid(address);
    setValidForSubmission(Boolean(isValid));
  }, [address, setValidForSubmission]);

  return (
    <Header onSubmit={handleSubmit}>
      <div data-cy="autocomplete-address-wrapper">
        <AutocompleteAddress
          address={address}
          onAddressChange={setAddress}
          countryCode={countryCode}
          apiKey={configuration?.googleMapsApiKey ?? ''}
          placeholders={
            placeholders || {
              manualAddressEntry: 'Search for address',
              street: 'Street',
              houseNumber: 'House Number',
              additionalInformation:
                'Additional information (C/O, apartment, ...)',
              postcode: 'Postcode',
              city: 'City',
            }
          }
          manualAddressEntryTexts={manualAddressEntryTexts}
        />
      </div>
    </Header>
  );
};
